import type { HeaderMenuAction } from '@/components/headerItems/types';

import type { SupportDraftInquiry } from '../model/supportContact';
import { useContactSupport } from './useContactSupport';

const INQUIRY_SUPPORT_DRAFT = 'I have a question about this inquiry: ';

/**
 * "Contact support" in an inquiry's "…" menu: opens the member's support
 * thread with a draft that carries this inquiry, so staff see which one they
 * mean without asking. Hidden, never removed, where the viewer cannot hold a
 * support thread or the conversation is not an inquiry they take part in, so
 * the menu keeps the same rows as the screen's state changes.
 *
 * The API accepts a reference only to an inquiry the member submitted or
 * received, so from any other inquiry (a sibling in an anonymous group, a
 * relay view) the thread opens with the draft alone rather than a chip the
 * send would refuse.
 */
export function useInquirySupportAction({
  inquiry,
  isReferenceable,
  isHidden,
}: {
  inquiry: SupportDraftInquiry;
  isReferenceable: boolean;
  isHidden: boolean;
}): HeaderMenuAction {
  const { canUseThread, contact, isOpening } = useContactSupport();

  return {
    key: 'contact-support',
    label: 'Contact support',
    sfIcon: 'questionmark.bubble',
    isDisabled: isOpening,
    isHidden: isHidden || !canUseThread,
    onPress: () =>
      contact({
        source: 'inquiry_menu',
        draft: INQUIRY_SUPPORT_DRAFT,
        inquiry: isReferenceable ? inquiry : undefined,
      }),
  };
}
